import { BubbleCoordinates, OpenPosition } from "./types";

type getEdgeCorrectedTargetXYFunctionSignature = (args: {
  coordinateArgs: getTargetXYFunctionInput;
  viewportWidth: number;
  viewportHeight: number;
}) => { coords: BubbleCoordinates };

export type getTargetXYFunctionInput = {
  pageX: number;
  pageY: number;
  width: number;
  height: number;
  modalBodyWidth: number;
  modalBodyHeight: number;
  openPosition: OpenPosition;
  verticalOffset?: number;
  horizontalOffset?: number;
};

type getTargetXYFunctionSignature = (
  args: getTargetXYFunctionInput
) => BubbleCoordinates;

export const getEdgeCorrectedTargetXYForModal: getEdgeCorrectedTargetXYFunctionSignature =
  ({ coordinateArgs, viewportWidth, viewportHeight }) => {
    const initialCoordinates =
      getTargetXYFromPagePositionAndButtonPosition(coordinateArgs);
    const { modalBodyWidth, modalBodyHeight, openPosition } = coordinateArgs;
    const offDirections = getOffDirectionsForEdgeOverflow({
      coords: initialCoordinates,
      viewportHeight,
      viewportWidth,
      modalHeight: modalBodyHeight,
      modalWidth: modalBodyWidth,
    });

    const needsEdgeCorrection = !!offDirections.length;
    const newOpenPosition = getNewOpenPositionIfOffDirectionsExist({
      offDirections,
      openPosition,
    });
    return needsEdgeCorrection
      ? {
          coords: getTargetXYFromPagePositionAndButtonPosition({
            ...coordinateArgs,
            openPosition: newOpenPosition,
          }),
        }
      : { coords: initialCoordinates };
  };

export type OffDirections = ("left" | "right" | "top" | "bottom")[];

export const isValidOpenPosition = (
  position: string
): position is OpenPosition => {
  const validPositions: OpenPosition[] = [
    "top-start",
    "top-end",
    "top-center",
    "bottom-start",
    "bottom-end",
    "bottom-center",
    "left-start",
    "left-end",
    "right-start",
    "right-end",
  ];
  return validPositions.includes(position as OpenPosition);
};

/**
 * This function is used to get the new open position if the off directions exist.
 * @param offDirections - The off directions.
 * @param openPosition - The open position.
 * @param isValid - The function to check if the position is valid.
 * @returns The new open position.
 *
 * 2025-03-06: this function now accurately handles the edge correction for the open position when there are 2 off directions.
 * the previous implementation had a bug where it would set the open
 * position only based upon the last off direction passed in.
 */
export const getNewOpenPositionIfOffDirectionsExist = ({
  offDirections,
  openPosition,
  isValid = isValidOpenPosition,
}: {
  offDirections: OffDirections;
  openPosition: OpenPosition;
  isValid?: (position: string) => position is OpenPosition;
}): OpenPosition => {
  let parts = openPosition.split("-");

  if (offDirections.length === 0) {
    return openPosition;
  } else if (offDirections.length === 1) {
    if (offDirections[0] === "left") {
      parts = parts.map((part) => (part === "left" ? "right" : part));
    } else if (offDirections[0] === "right") {
      parts = parts.map((part) => (part === "right" ? "left" : part));
    } else if (offDirections[0] === "top") {
      parts = parts.map((part) => (part === "top" ? "bottom" : part));
    } else if (offDirections[0] === "bottom") {
      parts = parts.map((part) =>
        part === "bottom" || part === "right" || part === "left" ? "top" : part
      );
    }
  } else if (offDirections.length === 2) {
    offDirections.forEach((direction) => {
      if (direction === "left") {
        parts = parts.map((part) =>
          part === "left" ? "right" : part === "end" ? "start" : part
        );
      } else if (direction === "right") {
        parts = parts.map((part) =>
          part === "right" ? "left" : part === "start" ? "end" : part
        );
      } else if (direction === "top") {
        parts = parts.map((part) => (part === "top" ? "bottom" : part));
      } else if (direction === "bottom") {
        parts = parts.map((part) =>
          part === "bottom" || part === "right" || part === "left"
            ? "top"
            : part
        );
      }
    });
  }

  const result = parts.join("-");
  if (isValid(result)) {
    return result;
  } else {
    return openPosition;
  }
};

export const getTargetXYFromPagePositionAndButtonPosition: getTargetXYFunctionSignature =
  ({
    pageX,
    pageY,
    width,
    height,
    modalBodyWidth,
    modalBodyHeight,
    openPosition,
    verticalOffset = 0,
    horizontalOffset = 0,
  }) => {
    switch (openPosition) {
      case "left-start":
        return {
          left: pageX - modalBodyWidth - 4 + horizontalOffset,
          top: pageY,
        };
      case "left-end":
        return {
          left: pageX - modalBodyWidth - 4 + horizontalOffset,
          top: pageY + height - modalBodyHeight,
        };
      case "right-start":
        return {
          left: pageX + width + 4 + horizontalOffset,
          top: pageY,
        };
      case "right-end":
        return {
          left: pageX + width + 4 + horizontalOffset,
          top: pageY + height - modalBodyHeight,
        };
      case "top-start":
        return {
          left: pageX + horizontalOffset,
          top: pageY - modalBodyHeight - 4 - verticalOffset,
        };
      case "top-end":
        return {
          left: pageX + width - modalBodyWidth + horizontalOffset,
          top: pageY - modalBodyHeight - 4 - verticalOffset,
        };
      case "top-center":
        return {
          left: pageX + width / 2 - modalBodyWidth / 2 + horizontalOffset,
          top: pageY - modalBodyHeight - 4 - verticalOffset,
        };
      case "bottom-end":
        return {
          left: pageX + width - modalBodyWidth + horizontalOffset,
          top: pageY + height + 4 + verticalOffset,
        };
      case "bottom-start": {
        return {
          left: pageX + horizontalOffset,
          top: pageY + height + 4 + verticalOffset,
        };
      }
      case "bottom-center":
        return {
          left: pageX + width / 2 - modalBodyWidth / 2 + horizontalOffset,
          top: pageY + height + 4,
        };
      default:
        return {
          left: pageX + horizontalOffset,
          top: pageY + height + 4,
        };
    }
  };

type getOffDirectionsForEdgeOverflowInput = {
  coords: BubbleCoordinates;
  modalWidth: number;
  modalHeight: number;
  viewportWidth: number;
  viewportHeight: number;
};
export const getOffDirectionsForEdgeOverflow = ({
  coords,
  modalWidth,
  modalHeight,
  viewportWidth,
  viewportHeight,
}: getOffDirectionsForEdgeOverflowInput) => {
  const offDirections: OffDirections = [];

  if (coords.left !== undefined && coords.left + modalWidth > viewportWidth) {
    offDirections.push("right");
  }
  if (coords.left !== undefined && coords.left < 0) {
    offDirections.push("left");
  }
  if (coords.top !== undefined && coords.top + modalHeight > viewportHeight) {
    offDirections.push("bottom");
  }
  if (coords.top !== undefined && coords.top < 0) {
    offDirections.push("top");
  }

  return offDirections;
};

const fadeUpAnimation = {
  from: { opacity: 0, top: 8 },
  animate: { opacity: 1, top: 0 },
  exit: { opacity: 0, top: 8 },
};
const fadeDownAnimation = {
  from: { opacity: 0, top: -8 },
  animate: { opacity: 1, top: 0 },
  exit: { opacity: 0, top: -8 },
};
const fadeLeftAnimation = {
  from: { opacity: 0, left: 8 },
  animate: { opacity: 1, left: 0 },
  exit: { opacity: 0, left: 8 },
};
const fadeRightAnimation = {
  from: { opacity: 0, left: -8 },
  animate: { opacity: 1, left: 0 },
  exit: { opacity: 0, left: -8 },
};

export const getAnimationFromOpenPosition = (pos: OpenPosition) => {
  switch (pos) {
    case "top-end":
    case "top-start":
    case "top-center":
      return fadeUpAnimation;
    case "left-end":
    case "left-start":
      return fadeLeftAnimation;
    case "right-end":
    case "right-start":
      return fadeRightAnimation;
    case "bottom-end":
    case "bottom-start":
    case "bottom-center":
    default:
      return fadeDownAnimation;
  }
};
